import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Upload, Grid, List, Filter, Clock,
  HardDrive, CheckCircle2, AlertCircle,
} from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { useRoleData } from '../../hooks/useRoleData';
import { contentAPI } from '../../services/api';
import { BASE_URL } from '../../constants/config';
import './ContentLibrary.css';

// Placeholder image URL
const PLACEHOLDER_IMAGE = `${BASE_URL}/uploads/assets/placehold_content.svg`;

const ContentLibrary = () => {
  const navigate = useNavigate();
  const { openUploadModal, currentUser } = useAppContext();
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [viewMode, setViewMode] = useState('grid');
  const [statusFilter, setStatusFilter] = useState('All');

  const { filteredContent, canUpload, refreshData, loading } = useRoleData();
  const [fetchedCategories, setFetchedCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await contentAPI.getCategories();
        setFetchedCategories(response.data.data);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Refresh content list when new content is uploaded
  useEffect(() => {
    const handleContentUploaded = () => {
      refreshData();
    };
    window.addEventListener('contentUploaded', handleContentUploaded);
    return () => window.removeEventListener('contentUploaded', handleContentUploaded);
  }, [refreshData]);

  const categories = ['All', ...fetchedCategories];

  const filtered = filteredContent.filter((c) => {
    const matchSearch =
      c.title.toLowerCase().includes(search.toLowerCase()) ||
      (c.subtitle && c.subtitle.toLowerCase().includes(search.toLowerCase())) ||
      c.description.toLowerCase().includes(search.toLowerCase()) ||
      c.tags.some((t) => t.toLowerCase().includes(search.toLowerCase()));
    const matchCategory = selectedCategory === 'All' || c.category === selectedCategory;
    const matchStatus = statusFilter === 'All' || c.status === statusFilter;
    return matchSearch && matchCategory && matchStatus;
  });

  return (
    <div className="content-lib">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1>
            {currentUser?.role === 'Trainee' ? 'Available Content' : 'Content Library'}
          </h1>
          <p>{filteredContent.length} VR experiences available</p>
        </div>
        {canUpload && (
          <button className="btn btn-primary" onClick={openUploadModal}>
            <Upload size={17} />
            Upload Content
          </button>
        )}
      </div>

      {/* Filters Bar */}
      <div className="content-lib__filters card">
        <div className="content-lib__search">
          <Search size={16} className="content-lib__search-icon" />
          <input
            className="content-lib__search-input"
            type="text"
            placeholder="Search content by title, tags..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="content-lib__filter-group">
          <Filter size={14} />
          <select
            className="content-lib__select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All Status</option>
            <option value="Active">Active</option>
            <option value="Draft">Draft</option>
          </select>
        </div>

        <div className="content-lib__view-toggle">
          <button
            className={`content-lib__view-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
          >
            <Grid size={16} />
          </button>
          <button
            className={`content-lib__view-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
          >
            <List size={16} />
          </button>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="content-lib__categories">
        {categories.slice(0, 7).map((cat) => (
          <button
            key={cat}
            className={`content-lib__cat-btn ${selectedCategory === cat ? 'active' : ''}`}
            onClick={() => setSelectedCategory(cat)}
          >
            {cat}
            {cat !== 'All' && (
              <span className="content-lib__cat-count">
                {filteredContent.filter((c) => c.category === cat).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Results Count */}
      {!loading && (
        <div className="content-lib__results-info">
          <span>Showing <strong>{filtered.length}</strong> of {filteredContent.length} items</span>
        </div>
      )}

      {/* Content Grid / List */}
      {loading ? (
        <div className="content-lib__loading">
          <div className="content-lib__spinner"></div>
          <p>Loading content...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><Search size={28} /></div>
          <h3>No content found</h3>
          <p>
            {currentUser?.role === 'Trainee' ? 'You are not enrolled in any modules with VR content.' :
             'Try adjusting your search or filters'}
          </p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="content-lib__grid">
          {filtered.map((content) => (
            <div
              key={content.id}
              className="content-lib__card card"
              onClick={() => navigate(`/content-library/${content.id}`, { state: { fromList: true } })}
            >
              <div className="content-lib__card-thumb">
                <img src={content.thumbnail || PLACEHOLDER_IMAGE} alt={content.title} />
                <span className={`content-lib__status-dot ${content.status === 'Active' ? 'active' : 'draft'}`} />
              </div>
              <div className="content-lib__card-body">
                <div className="content-lib__card-top">
                  <span className="badge badge-green">{content.category}</span>
                  <span className="badge badge-dark">{content.platform}</span>
                </div>
                <h3 className="content-lib__card-title">{content.title}</h3>
                {content.subtitle && <p className="content-lib__card-subtitle">{content.subtitle}</p>}
                <p className="content-lib__card-desc">{content.description}</p>
                <div className="content-lib__card-tags">
                  {content.tags.slice(0, 3).map((t) => (
                    <span key={t} className="content-lib__tag">#{t}</span>
                  ))}
                </div>
                <div className="content-lib__card-footer">
                  <span><Clock size={12} /> {content.duration} min</span>
                  {content.fileSize && <span><HardDrive size={12} /> {content.fileSize}</span>}
                  {content.contentType === 'web_virtual_tour' ? (
                    <span>🌐 Web Tour</span>
                  ) : content.apkVersion ? (
                    <span>v{content.apkVersion}</span>
                  ) : null}
                </div>
                <div className="content-lib__card-completions">
                  {content.status === 'Active' ? (
                    <CheckCircle2 size={12} className="content-lib__status-icon--active" />
                  ) : (
                    <AlertCircle size={12} className="content-lib__status-icon--draft" />
                  )}
                  <span>{content.completionCount} completions</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card">
          <div className="content-lib__list">
            {filtered.map((content, idx) => (
              <div
                key={content.id}
                className={`content-lib__list-item ${idx < filtered.length - 1 ? 'content-lib__list-item--bordered' : ''}`}
                onClick={() => navigate(`/content-library/${content.id}`, { state: { fromList: true } })}
              >
                <div className="content-lib__list-thumb">
                  <img src={content.thumbnail || PLACEHOLDER_IMAGE} alt={content.title} />
                </div>
                <div className="content-lib__list-info">
                  <span className="content-lib__list-title">{content.title}</span>
                  {content.subtitle && <span className="content-lib__list-subtitle">{content.subtitle}</span>}
                  <div className="content-lib__list-meta">
                    <span className="badge badge-green">{content.category}</span>
                    <span><Clock size={11} /> {content.duration} min</span>
                    {content.fileSize && <span><HardDrive size={11} /> {content.fileSize}</span>}
                    <span>{content.platform}</span>
                    {content.contentType === 'web_virtual_tour' ? (
                      <span className="badge badge-dark">🌐 Web Tour</span>
                    ) : content.apkVersion ? (
                      <span className="badge badge-dark">v{content.apkVersion}</span>
                    ) : null}
                  </div>
                </div>
                <div className="content-lib__list-stats">
                  <span>{content.completionCount} completions</span>
                  <span className={`badge ${content.status === 'Active' ? 'badge-green' : 'badge-gray'}`}>
                    {content.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentLibrary;
